import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import { Part, Model, ModelCategory, Manufacturer, BillOfMaterials, WorkOrder, AuditLogEntry, DeviceManagementDevice } from '../types';

const API_BASE_URL = 'https://17igbr2mm5.execute-api.eu-west-1.amazonaws.com/dev';

const getAuthHeaders = async (): Promise<Record<string, string>> => {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch (error) {
    console.error('Error getting auth token:', error);
    return {};
  }
};

// Create axios instance with base configuration
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

export class PartsService {
  static async getAllParts(): Promise<Part[]> {
    try {
      const headers = await getAuthHeaders();
      const response = await apiClient.get('/parts', { headers });
      return response.data;
    } catch (error) {
      console.error('Error fetching parts:', error);
      throw error;
    }
  }

  static async getPartById(id: string): Promise<Part> {
    try {
      const headers = await getAuthHeaders();
      const response = await apiClient.get(`/parts/${id}`, { headers });
      return response.data;
    } catch (error) {
      console.error('Error fetching part:', error);
      throw error;
    }
  }

  static async createPart(part: Omit<Part, 'id'>): Promise<Part> {
    try {
      const headers = await getAuthHeaders();
      const response = await apiClient.post('/parts', part, { headers });
      return response.data;
    } catch (error) {
      console.error('Error creating part:', error);
      throw error;
    }
  }

  static async updatePart(id: string, part: Partial<Part>): Promise<Part> {
    try {
      const headers = await getAuthHeaders();
      const response = await apiClient.put(`/parts/${id}`, part, { headers });
      return response.data;
    } catch (error) {
      console.error('Error updating part:', error);
      throw error;
    }
  }

  static async deletePart(id: string): Promise<void> {
    try {
      const headers = await getAuthHeaders();
      await apiClient.delete(`/parts/${id}`, { headers });
    } catch (error) {
      console.error('Error deleting part:', error);
      throw error;
    }
  }
}

export class DevicesService {
  static async getAllDevices(): Promise<DeviceManagementDevice[]> {
    try {
      const headers = await getAuthHeaders();
      const response = await apiClient.get('/devices', { headers });
      return response.data;
    } catch (error) {
      console.error('Error fetching devices:', error);
      throw error;
    }
  }

  static async getDeviceById(id: string): Promise<DeviceManagementDevice> {
    try {
      const headers = await getAuthHeaders();
      const response = await apiClient.get(`/devices/${id}`, { headers });
      return response.data;
    } catch (error) {
      console.error('Error fetching device:', error);
      throw error;
    }
  }

  static async getDeviceBySerialNumber(serialNumber: string): Promise<DeviceManagementDevice | null> {
    try {
      const devices = await this.getAllDevices();
      return devices.find(device => device.serialNumber === serialNumber) || null;
    } catch (error) {
      console.error('Error finding device by serial number:', error);
      return null;
    }
  }
}

export class WorkOrdersService {
  static async getAllWorkOrders(): Promise<WorkOrder[]> {
    try {
      const headers = await getAuthHeaders();
      const response = await apiClient.get('/work-orders', { headers });
      return response.data;
    } catch (error) {
      console.error('Error fetching work orders:', error);
      throw error;
    }
  }

  static async getWorkOrderById(id: string): Promise<WorkOrder> {
    try {
      const headers = await getAuthHeaders();
      const response = await apiClient.get(`/work-orders/${id}`, { headers });
      return response.data;
    } catch (error) {
      console.error('Error fetching work order:', error);
      throw error;
    }
  }

    static async getWorkOrdersByStatus(status: string): Promise<WorkOrder[]> {
    try {
      const headers = await getAuthHeaders();
      const response = await apiClient.get(`/work-orders?status=${status}`, { headers });
      return response.data;
    } catch (error) {
      console.error('Error fetching work orders by status:', error);
      throw error;
    }
  }

  static async getWorkOrdersByDevice(deviceId: string): Promise<WorkOrder[]> {
    try {
      const headers = await getAuthHeaders();
      const response = await apiClient.get(`/work-orders?deviceId=${deviceId}`, { headers });
      return response.data;
    } catch (error) {
      console.error('Error fetching work orders by device:', error);
      throw error;
    }
  }

  static async createWorkOrder(workOrder: Omit<WorkOrder, 'id' | 'createdAt' | 'updatedAt' | 'createdBy'>): Promise<WorkOrder> {
    try {
      const headers = await getAuthHeaders();
      const response = await apiClient.post('/work-orders', workOrder, { headers });
      return response.data;
    } catch (error) {
      console.error('Error creating work order:', error);
      throw error;
    }
  }

    static async updateWorkOrder(id: string, workOrder: Partial<WorkOrder>): Promise<WorkOrder> {
    try {
      const headers = await getAuthHeaders();
      const response = await apiClient.put(`/work-orders/${id}`, workOrder, { headers });
      return response.data;
    } catch (error) {
      console.error('Error updating work order:', error);
      throw error;
    }
  }

  static async deleteWorkOrder(id: string): Promise<void> {
    try {
      const headers = await getAuthHeaders();
      await apiClient.delete(`/work-orders/${id}`, { headers });
    } catch (error) {
      console.error('Error deleting work order:', error);
      throw error;
    }
  }
  

}

export class ModelsService {
  static async getAllModels(): Promise<Model[]> {
    try {
      const headers = await getAuthHeaders();
      const response = await apiClient.get('/models', { headers });
      return response.data;
    } catch (error) {
      console.error('Error fetching models:', error);
      throw error;
    }
  }

    static async getModelById(id: string): Promise<Model> {
    try {
      const headers = await getAuthHeaders();
      const response = await apiClient.get(`/models/${id}`, { headers });
      return response.data;
    } catch (error) {
      console.error('Error fetching model:', error);
      throw error;
    }
  }

    static async createModel(model: Omit<Model, 'id' | 'createdDate' | 'createdBy' | 'lastModified' | 'lastModifiedBy'>): Promise<Model> {
    try {
      const headers = await getAuthHeaders();
      const response = await apiClient.post('/models', model, { headers });
      return response.data;
    } catch (error) {
      console.error('Error creating model:', error);
      throw error;
    }
  }

    static async updateModel(id: string, model: Partial<Omit<Model, 'id' | 'createdDate' | 'createdBy' | 'lastModified' | 'lastModifiedBy'>>): Promise<Model> {
    try {
      const headers = await getAuthHeaders();
      const response = await apiClient.put(`/models/${id}`, model, { headers });
      return response.data;
    } catch (error) {
      console.error('Error updating model:', error);
      throw error;
    }
  }

    static async deleteModel(id: string): Promise<void> {
    try {
      const headers = await getAuthHeaders();
      await apiClient.delete(`/models/${id}`, { headers });
    } catch (error) {
      console.error('Error deleting model:', error);
      throw error;
    }
  }


}

export class BOMService {
  static async getAllBOMs(): Promise<BillOfMaterials[]> {
    try {
      const headers = await getAuthHeaders();
      const response = await apiClient.get('/boms', { headers });
      return response.data;
    } catch (error) {
      console.error('Error fetching BOMs:', error);
      throw error;
    }
  }
  
  static async getBOMById(id: string): Promise<BillOfMaterials> {
    try {
      const headers = await getAuthHeaders();
      const response = await apiClient.get(`/boms/${id}`, { headers });
      return response.data;
    } catch (error) {
      console.error('Error fetching BOM:', error);
      throw error;
    }
  }
  
  static async createBOM(bom: Omit<BillOfMaterials, 'id' | 'createdDate' | 'createdBy' | 'lastModified' | 'lastModifiedBy'>): Promise<BillOfMaterials> {
    try {
      const headers = await getAuthHeaders();
      const response = await apiClient.post('/boms', bom, { headers });
      return response.data;
    } catch (error) {
      console.error('Error creating BOM:', error);
      throw error;
    }
  }
  
  static async updateBOM(id: string, bom: Partial<BillOfMaterials>): Promise<BillOfMaterials> {
    try {
      const headers = await getAuthHeaders();
      const response = await apiClient.put(`/boms/${id}`, bom, { headers });
      return response.data;
    } catch (error) {
      console.error('Error updating BOM:', error);
      throw error;
    }
  }

  static async deleteBOM(id: string): Promise<void> {
    try {
      const headers = await getAuthHeaders();
      await apiClient.delete(`/boms/${id}`, { headers });
    } catch (error) {
      console.error('Error deleting BOM:', error);
      throw error;
    }
  }
}

export class ModelCategoryService {
  static async getAllModelCategories(): Promise<ModelCategory[]> {
    try {
      const headers = await getAuthHeaders();
      const response = await apiClient.get('/model-categories', { headers });
      return response.data;
    } catch (error) {
      console.error('Error fetching model categories:', error);
      throw error;
    }
  }

  static async getModelCategoryById(id: string): Promise<ModelCategory> {
    try {
      const headers = await getAuthHeaders();
      const response = await apiClient.get(`/model-categories/${id}`, { headers });
      return response.data;
    } catch (error) {
      console.error('Error fetching model category:', error);
      throw error;
    }
  }

  static async createModelCategory(category: Omit<ModelCategory, 'id' | 'createdDate' | 'createdBy' | 'lastModified' | 'lastModifiedBy'>): Promise<ModelCategory> {
    try {
      const headers = await getAuthHeaders();
      const response = await apiClient.post('/model-categories', category, { headers });
      return response.data;
    } catch (error) {
      console.error('Error creating model category:', error);
      throw error;
    }
  }

  static async updateModelCategory(id: string, category: Partial<Omit<ModelCategory, 'id' | 'createdDate' | 'createdBy' | 'lastModified' | 'lastModifiedBy'>>): Promise<ModelCategory> {
    try {
      const headers = await getAuthHeaders();
      const response = await apiClient.put(`/model-categories/${id}`, category, { headers });
      return response.data;
    } catch (error) {
      console.error('Error updating model category:', error);
      throw error;
    }
  }

  static async deleteModelCategory(id: string): Promise<void> {
    try {
      const headers = await getAuthHeaders();
      await apiClient.delete(`/model-categories/${id}`, { headers });
    } catch (error) {
      console.error('Error deleting model category:', error);
      throw error;
    }
  }
}

export class ManufacturerService {
  static async getAllManufacturers(): Promise<Manufacturer[]> {
    try {
      const headers = await getAuthHeaders();
      const response = await apiClient.get('/manufacturers', { headers });
      return response.data;
    } catch (error) {
      console.error('Error fetching manufacturers:', error);
      throw error;
    }
  }

  static async getManufacturerById(id: string): Promise<Manufacturer> {
    try {
      const headers = await getAuthHeaders();
      const response = await apiClient.get(`/manufacturers/${id}`, { headers });
      return response.data;
    } catch (error) {
      console.error('Error fetching manufacturer:', error);
      throw error;
    }
  }

  static async createManufacturer(manufacturer: Omit<Manufacturer, 'id' | 'createdDate' | 'createdBy' | 'lastModified' | 'lastModifiedBy'>): Promise<Manufacturer> {
    try {
      const headers = await getAuthHeaders();
      const response = await apiClient.post('/manufacturers', manufacturer, { headers });
      return response.data;
    } catch (error) {
      console.error('Error creating manufacturer:', error);
      throw error;
    }
  }

  static async updateManufacturer(id: string, manufacturer: Partial<Omit<Manufacturer, 'id' | 'createdDate' | 'createdBy' | 'lastModified' | 'lastModifiedBy'>>): Promise<Manufacturer> {
    try {
      const headers = await getAuthHeaders();
      const response = await apiClient.put(`/manufacturers/${id}`, manufacturer, { headers });
      return response.data;
    } catch (error) {
      console.error('Error updating manufacturer:', error);
      throw error;
    }
  }

  static async deleteManufacturer(id: string): Promise<void> {
    try {
      const headers = await getAuthHeaders();
      await apiClient.delete(`/manufacturers/${id}`, { headers });
    } catch (error) {
      console.error('Error deleting manufacturer:', error);
      throw error;
    }
  }
}

export class AuditLogService {
  static async getAllAuditLogs(params?: {
    entityType?: string;
    startDate?: string;
    endDate?: string;
    limit?: number;
  }): Promise<AuditLogEntry[]> {
    try {
      const headers = await getAuthHeaders();
      const queryParams = new URLSearchParams();
      
      if (params?.entityType && params.entityType !== 'all') {
        queryParams.append('entityType', params.entityType);
      }
      if (params?.startDate) {
        queryParams.append('startDate', params.startDate);
      }
      if (params?.endDate) {
        queryParams.append('endDate', params.endDate);
      }
      if (params?.limit) {
        queryParams.append('limit', params.limit.toString());
      }

      const url = `/audit-log${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await apiClient.get(url, { headers });
      return response.data;
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      throw error;
    }
  }
}

export class WorkOrderTypeDefaultsService {
  static async getDefaultsForType(workOrderType: string): Promise<import('../types').WorkOrderTypeDefaults | null> {
    try {
      const headers = await getAuthHeaders();
      const response = await apiClient.get(`/work-order-defaults/${workOrderType}`, { headers });
      return response.data;
    } catch (error) {
      console.error('Error fetching work order type defaults:', error);
      // Return null if not found, rather than throwing
      return null;
    }
  }

  static async getAllDefaults(): Promise<import('../types').WorkOrderTypeDefaults[]> {
    try {
      const headers = await getAuthHeaders();
      const response = await apiClient.get('/work-order-defaults', { headers });
      return response.data;
    } catch (error) {
      console.error('Error fetching all work order defaults:', error);
      throw error;
    }
  }

  static async createDefaults(defaults: Omit<import('../types').WorkOrderTypeDefaults, 'id' | 'createdDate' | 'createdBy' | 'lastModified' | 'lastModifiedBy'>): Promise<import('../types').WorkOrderTypeDefaults> {
    try {
      const headers = await getAuthHeaders();
      const response = await apiClient.post('/work-order-defaults', defaults, { headers });
      return response.data;
    } catch (error) {
      console.error('Error creating work order defaults:', error);
      throw error;
    }
  }

  static async updateDefaults(id: string, defaults: Partial<import('../types').WorkOrderTypeDefaults>): Promise<import('../types').WorkOrderTypeDefaults> {
    try {
      const headers = await getAuthHeaders();
      const response = await apiClient.put(`/work-order-defaults/${id}`, defaults, { headers });
      return response.data;
    } catch (error) {
      console.error('Error updating work order defaults:', error);
      throw error;
    }
  }

  static async deleteDefaults(id: string): Promise<void> {
    try {
      const headers = await getAuthHeaders();
      await apiClient.delete(`/work-order-defaults/${id}`, { headers });
    } catch (error) {
      console.error('Error deleting work order defaults:', error);
      throw error;
    }
  }
}
